import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dna, Sparkles, AlertTriangle, ChevronRight, Zap } from "lucide-react";

const SkillDNAWidget = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [dnaData, setDnaData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchDNAData();
    }
  }, [token]);

  const fetchDNAData = async () => {
    try {
      const response = await axios.get(`${API}/skill-dna/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDnaData(response.data);
    } catch (error) {
      // DNA profile doesn't exist yet, that's okay
      if (error.response?.status === 404) {
        setDnaData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="animate-pulse border-2 border-indigo-100">
        <CardHeader>
          <div className="h-6 bg-gray-200 rounded w-1/2"></div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!dnaData) {
    return (
      <Card className="border-2 border-indigo-200 hover:border-indigo-400 transition-all duration-300 bg-gradient-to-br from-white to-indigo-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Dna className="w-6 h-6 text-indigo-600" />
            Discover Your Skill DNA
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Unlock your genetic career map with multi-dimensional skill profiling
          </p>
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-green-500" />
            <span>Identify natural strengths</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            <span>Find growth opportunities</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Zap className="w-4 h-4 text-purple-500" />
            <span>Get career compatibility</span>
          </div>
          <Button
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            onClick={() => navigate('/skill-dna')}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Generate My DNA Profile
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 75) return "text-green-600";
    if (score >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBg = (score) => {
    if (score >= 75) return "bg-green-100";
    if (score >= 50) return "bg-yellow-100";
    return "bg-red-100";
  };

  return (
    <Card className="border-2 border-indigo-200 hover:border-indigo-400 transition-all duration-300 bg-gradient-to-br from-white to-indigo-50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="relative">
              <Dna className="w-6 h-6 text-indigo-600" />
              <Sparkles className="w-3 h-3 text-yellow-400 absolute -top-1 -right-1 animate-pulse" />
            </div>
            Your Skill DNA
          </CardTitle>
          <Badge className={`${getScoreBg(dnaData.dna_strength_score)} ${getScoreColor(dnaData.dna_strength_score)}`}>
            {dnaData.dna_strength_score}/100
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* DNA Strength Meter */}
        <div className="bg-white p-3 rounded-lg border border-indigo-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">DNA Strength</span>
            <span className={`text-xl font-bold ${getScoreColor(dnaData.dna_strength_score)}`}>
              {dnaData.dna_strength_score}
            </span>
          </div>
          <Progress value={dnaData.dna_strength_score} className="h-2" />
          <p className="text-xs text-gray-500 mt-1">
            {dnaData.completion_percentage}% profile complete
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-green-600" />
              <p className="text-xs text-gray-600">Skill Genes</p>
            </div>
            <p className="text-2xl font-bold text-green-700">{dnaData.total_genes}</p>
            <p className="text-xs text-gray-500">Natural strengths</p>
          </div>
          <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-orange-600" />
              <p className="text-xs text-gray-600">Mutations</p>
            </div>
            <p className="text-2xl font-bold text-orange-700">{dnaData.total_mutations}</p>
            <p className="text-xs text-gray-500">Growth areas</p>
          </div>
        </div>

        {/* Top Career Match */}
        {dnaData.top_career_match && (
          <div className="bg-white p-3 rounded-lg border border-indigo-100">
            <p className="text-xs text-gray-600 mb-1">Top Career Match</p>
            <p className="font-semibold text-indigo-900">{dnaData.top_career_match.career_title}</p>
            <div className="flex items-center gap-2 mt-2">
              <Progress value={dnaData.top_career_match.compatibility_score} className="flex-1 h-1.5" />
              <span className="text-xs font-semibold text-indigo-600">
                {dnaData.top_career_match.compatibility_score}%
              </span>
            </div>
          </div>
        )}

        {/* Learning & Personality */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          {dnaData.learning_style && (
            <div className="bg-purple-50 p-2 rounded">
              <p className="text-gray-600">Learning</p>
              <p className="font-semibold capitalize">{dnaData.learning_style}</p>
            </div>
          )}
          {dnaData.dominant_trait && (
            <div className="bg-pink-50 p-2 rounded">
              <p className="text-gray-600">Personality</p>
              <p className="font-semibold">{dnaData.dominant_trait}</p>
            </div>
          )}
        </div>

        {/* CTA Button */}
        <Button
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
          onClick={() => navigate('/skill-dna')}
        >
          <Dna className="w-4 h-4 mr-2" />
          View Full DNA Profile
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default SkillDNAWidget;
